import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import type { OrgOpSignableDecision } from '@mls-wallet/shared-types';
import { Signature } from '@mls-wallet/database';

type CreateAccountSignatureArg = {
  proposalId: string;
  decision: OrgOpSignableDecision;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: CreateAccountSignatureArg }
): Promise<Signature[]> => {
  const { proposalId, decision } = arg;
  return await denApi.post<Signature[], { decision: OrgOpSignableDecision }>(
    `admin/multichain-account/proposals/${encodeURIComponent(proposalId)}/review`,
    { decision }
  );
};

/**
 * Hook to review (approve or reject) an account proposal.
 *
 * @example
 * ```tsx
 * import { OrgOperationDecision } from '@mls-wallet/shared-types';
 *
 * const { createSignature, isLoading } = useAccountProposalSignature();
 *
 * const handleApprove = async () => {
 *   await createSignature({ proposalId, decision: OrgOperationDecision.APPROVE });
 * };
 * ```
 */
export function useAccountProposalSignature(
  swrOptions?: SWRMutationConfiguration<
    Signature[],
    Error,
    string,
    CreateAccountSignatureArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    Signature[],
    Error,
    string,
    CreateAccountSignatureArg
  >('admin/multichain-account/proposals/signature', fetcher, swrOptions);

  return { createSignature: trigger, isLoading: isMutating, error };
}
